import React from 'react';
import { triggerAutoRenewal, checkTriggerStatus } from '../../providers/RenewalAPI';
import { getEnvironment, Environment } from '../../config/EnvironmentConfig';
import { COOLDOWN_SECONDS } from '../../constants/RenewalPolling';

function resolveEnvironmentName(): string {
  if (getEnvironment() === Environment.DEV) {
    return 'dev';
  } else if (getEnvironment() === Environment.PRESTAGE) {
    return 'prestage';
  } else {
    return 'dev';
  }
}

export interface AutoRenewalCoolDown {
  isCoolDown: boolean;
  countDownTimer: number;
  checkCoolDownStatus: () => Promise<any>;
  handleCoolDown: (isCoolDown: boolean, accountId?: string) => Promise<any>;
}

// Shared by RecordsPage and RetailResourcePage, which each drove an identical
// environment-wide auto-renewal cooldown/countdown independently.
//
// This countdown only gates re-triggering (don't let a partner spam the button) — it is
// intentionally decoupled from whether a renewal is actually still in flight, which is
// tracked separately by the polled RenewalStatusPill/renewalPollerRegistry. Don't re-merge
// these: a renewal can realistically finish before or well after this cooldown expires.
export function useAutoRenewalCoolDown(): AutoRenewalCoolDown {
  const [isCoolDown, setIsCoolDown] = React.useState(false);
  const [countDownTimer, setCountDownTimer] = React.useState(COOLDOWN_SECONDS);

  function startCountdownTimer() {
    const startCounting = setInterval(() => {
      setCountDownTimer(previous => {
        const next = previous - 1;
        if (next <= 0) {
          setIsCoolDown(false);
          clearInterval(startCounting);
          return COOLDOWN_SECONDS;
        }
        return next;
      });
    }, 1000);
  }

  const checkCoolDownStatus = () => {
    return new Promise(function (resolve) {
      const triggerAutoRenewalBody = {
        environment: resolveEnvironmentName()
      }
      checkTriggerStatus(triggerAutoRenewalBody).then((response: any) => {
        if (response.httpstatus == 200) {
          setIsCoolDown(response.cooldown)
        } else {
          setIsCoolDown(false)
        }
        resolve(true)
      }).catch(() => {
        // A network-level failure (fetch rejected outright) must not leave this promise
        // hanging forever — callers (e.g. RecordsPage's setView) await this before
        // clearing their loading state.
        setIsCoolDown(false)
        resolve(true)
      });
    });
  }

  const handleCoolDown = (nextIsCoolDown: boolean, accountId?: string): Promise<any> => {
    setIsCoolDown(nextIsCoolDown)
    if (nextIsCoolDown) {
      const triggerAutoRenewalBody = {
        environment: resolveEnvironmentName(),
        ...(accountId ? { accountId } : {})
      }

      return triggerAutoRenewal(triggerAutoRenewalBody).then((response: any) => {
        if (response.httpstatus >= 200 && response.httpstatus < 300) {
          startCountdownTimer()
        } else {
          setIsCoolDown(false)
        }
        return response;
      }).catch(() => {
        // A network-level failure (fetch rejected outright) must not leave isCoolDown
        // stuck true forever, nor bubble up as an unhandled rejection to callers.
        setIsCoolDown(false)
        return { httpstatus: 0 };
      });
    }
    return Promise.resolve({ httpstatus: 200 });
  };

  return { isCoolDown, countDownTimer, checkCoolDownStatus, handleCoolDown };
}
